import { fmtPrice, fmtShares } from '../../components/charts/chartFormat';
import type { PanelId, Unit } from './seriesTypes';

function formatUsd(value: number): string {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
}

function formatPriceValue(value: number): string {
    const abs = Math.abs(value);
    const digits = abs >= 1_000 ? 0 : abs >= 1 ? 2 : 4;
    const text = abs.toLocaleString('en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
    return `${value < 0 ? '-' : ''}$${text}`;
}

// Event clock time: a 0–1 share or small raw sums of squared log-odds moves.
function formatClockValue(value: number): string {
    if (value === 0) return '0';
    return Math.abs(value) >= 0.1 ? value.toFixed(2) : value.toPrecision(3);
}

export function formatValue(unit: Unit, value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '–';
    switch (unit) {
        case 'probability':
            return `${(value * 100).toFixed(1)}%`;
        case 'usd':
            return formatUsd(value);
        case 'shares':
            return fmtShares(value);
        case 'price':
            return formatPriceValue(value);
        case 'bps':
            return `${value.toFixed(1)} bp`;
        case 'clock':
            return formatClockValue(value);
        case 'budget':
        case 'rate':
            return value.toFixed(3);
    }
}

function trimZero(text: string): string {
    return text.replace(/\.0$/, '');
}

function formatUsdTick(value: number): string {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 1_000_000) {
        return `${sign}$${trimZero((abs / 1_000_000).toFixed(1))}M`;
    }
    if (abs >= 1_000) return `${sign}$${trimZero((abs / 1_000).toFixed(1))}k`;
    return `${sign}$${abs.toFixed(0)}`;
}

export function formatTick(unit: Unit, value: number): string {
    if (unit === 'probability') return `${Math.round(value * 100)}%`;
    if (unit === 'usd') return formatUsdTick(value);
    if (unit === 'price') return fmtPrice(value);
    return formatValue(unit, value);
}

const PANEL_TITLES: Readonly<Record<string, string>> = {
    probability: 'Probability',
    clock: 'Event clock',
    flow: 'Flow',
    position: 'Position',
};

export function panelTitle(id: PanelId, fallback: string): string {
    return PANEL_TITLES[id] ?? fallback;
}
